<template>
<section>
    <div class="px-4 py-8 mx-auto max-w-5xl lg:py-16 xl:px-0">
        <!-- Main Headline -->
        <div class="mb-8 text-center">
            <h1 class="text-2xl leading-none text-center sm:text-3xl lg:text-32 font-ww-bold xl:px-12" v-html="mainHeadline">
            </h1>
        </div>

        <!-- Introduction Text -->
        <div class="mb-8 space-y-4 text-center">
            <div class="max-w-2xl text-base text-left lg:text-lg sm:text-base font-ww-regular" v-html="introText1">
            </div>
            <div class="max-w-2xl text-base text-left lg:text-lg sm:text-base font-ww-regular" v-html="introText2">
            </div>
        </div>

        <!-- Caffeine Content Section -->
        <div class="mb-12">
            <h2 class="mb-4 text-xl leading-5 text-center md:mb-8 xl:text-2xl font-ww-bold" v-html="caffeineHeading">
            </h2>

            <div class="grid gap-y-4 gap-6 gap-x-6 mx-auto max-w-5xl md:gap-x-10 md:gap-y-6 md:grid-cols-3">
                <div v-for="(item, index) in caffeineItems" :key="index" class="flex gap-x-2 text-center lg:gap-x-4">
                    <div class="flex justify-left">
                        <div class="flex relative justify-center items-center w-16 h-20 rounded-lg">
                            <nuxt-img :src="item.iconSrc" :alt="item.iconAlt" :width="item.iconWidth" class="md-max:w-8 md-max:h-12" :height="item.iconHeight" loading="lazy" />
                        </div>
                    </div>
                    <div class="flex md:items-start items-center text-sm font-ww-regular !leading-5 text-left xl:text-lg sm:text-base" v-html="item.description">
                    </div>
                </div>
            </div>
        </div>

        <!-- Bottom Text -->
        <div class="mx-auto space-y-6 text-left">
            <div class="text-sm lg:text-lg sm:text-base" v-html="bottomText1">
            </div>
            <div class="text-sm lg:text-lg sm:text-base" v-html="bottomText2">
            </div>
        </div>
    </div>
</section>
</template>

<script setup>
defineProps({
  mainHeadline: {
    type: String,
    default: '"Afternoon Crashes Are Normal, Right? Wrong! You Can Unlock All-Day, Focused Energy Without the Slumps."'
  },
  introText1: {
    type: String,
    default: 'Hundreds of studies have shown the benefits of coffee and caffeine.'
  },
  introText2: {
    type: String,
    default: 'Yet, anything taken to the extreme, starts working backward.'
  },
  caffeineHeading: {
    type: String,
    default: 'Here\'s the amount of caffeine in common beverages:'
  },
  caffeineItems: {
    type: Array,
    default: () => [
      {
        iconSrc: '/assets/images/energy-drink.svg',
        iconAlt: 'Energy Drink',
        iconWidth: 35,
        iconHeight: 66,
        description: 'Energy drinks: 160 to 215 mg (2 to 2.5X a cup of coffee)'
      },
      {
        iconSrc: '/assets/images/iced-latte.svg',
        iconAlt: 'Iced Latte',
        iconWidth: 53,
        iconHeight: 76,
        description: 'Iced latte drink at the most popular coffee shop: Up to 300 mg of caffeine (3X a cup of coffee)'
      },
      {
        iconSrc: '/assets/images/low-quality.svg',
        iconAlt: 'Low Quality Coffee',
        iconWidth: 58,
        iconHeight: 65,
        description: 'Low quality Robusta coffee (common in many cheap drinks): 265 mg (2.5X a cup of high-quality coffee)'
      }
    ]
  },
  bottomText1: {
    type: String,
    default: 'You crash in the afternoon because of what you drank in the morning (and throughout the day).'
  },
  bottomText2: {
    type: String,
    default: 'Low-quality coffee, sugary energy drinks, and "naked" coffee without modulating enhancers to extend and smooth out caffeine\'s peaks and valleys.'
  }
})

defineBuilderComponent({
    friendlyName: 'Headline With Icon List',
    canHaveChildren: false,
    inputs: [
        {
            name: 'mainHeadline',
            type: 'richText',
            defaultValue: '"Afternoon Crashes Are Normal, Right? Wrong! You Can Unlock All-Day, Focused Energy Without the Slumps."'
        },
        {
            name: 'introText1',
            type: 'richText',
            defaultValue: 'Hundreds of studies have shown the benefits of coffee and caffeine.'
        },
        {
            name: 'introText2',
            type: 'richText',
            defaultValue: 'Yet, anything taken to the extreme, starts working backward.'
        },
        {
            name: 'caffeineHeading',
            type: 'richText',
            defaultValue: 'Here\'s the amount of caffeine in common beverages:'
        },
        {
            name: 'caffeineItems',
            type: 'list',
            subFields: [
                {
                    name: 'iconSrc',
                    type: 'file',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
                    defaultValue: ''
                },
                {
                    name: 'iconAlt',
                    type: 'text',
                    defaultValue: ''
                },
                {
                    name: 'iconWidth',
                    type: 'number',
                    defaultValue: 50
                },
                {
                    name: 'iconHeight',
                    type: 'number',
                    defaultValue: 50
                },
                {
                    name: 'description',
                    type: 'richText',
                    defaultValue: ''
                }
            ],
            defaultValue: [
                {
                    iconSrc: '/assets/images/energy-drink.svg',
                    iconAlt: 'Energy Drink',
                    iconWidth: 35,
                    iconHeight: 66,
                    backgroundColor: '#3B82F6',
                    description: 'Energy drinks: 160 to 215 mg (2 to 2.5X a cup of coffee)'
                },
                {
                    iconSrc: '/assets/images/iced-latte.svg',
                    iconAlt: 'Iced Latte',
                    iconWidth: 53,
                    iconHeight: 76,
                    backgroundColor: '#3B82F6',
                    description: 'Iced latte drink at the most popular coffee shop: Up to 300 mg of caffeine (3X a cup of coffee)'
                },
                {
                    iconSrc: '/assets/images/low-quality.svg',
                    iconAlt: 'Low Quality Coffee',
                    iconWidth: 58,
                    iconHeight: 65,
                    backgroundColor: '#3B82F6',
                    description: 'Low quality Robusta coffee (common in many cheap drinks): 265 mg (2.5X a cup of high-quality coffee)'
                }
            ]
        },
        {
            name: 'bottomText1',
            type: 'richText',
            defaultValue: 'You crash in the afternoon because of what you drank in the morning (and throughout the day).'
        },
        {
            name: 'bottomText2',
            type: 'richText',
            defaultValue: 'Low-quality coffee, sugary energy drinks, and "naked" coffee without modulating enhancers to extend and smooth out caffeine\'s peaks and valleys.'
        }
    ]
})
</script>